import { useState, type FormEvent } from 'react';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qsn8fnahb7382ovf4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, v as string])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';

      if (
        response.ok &&
        parsed &&
        parsed.code === 'OK'
      ) {
        setStatus('success');
        form.reset();
      } else if (
        !response.ok ||
        serverMsg.includes('spam') ||
        serverMsg.includes('form data is spam')
      ) {
        setStatus('error');
        setErrorMessage(serverMsg || '提交失败，请稍后重试。');
      } else {
        setStatus('error');
        setErrorMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setErrorMessage('网络错误，请检查连接后重试。');
    }
  };

  return (
    <section className="mb-16" id="contact">
      <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-8 text-center">联系我们</h2>
      <div className="max-w-2xl mx-auto">
        <div className="bg-background-100 rounded-lg border border-background-200/70 p-8">
          <p className="text-sm text-foreground-600 mb-6 text-center">
            有任何问题、建议或合作意向？请填写以下表单，我们会尽快回复您。
          </p>
          <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                主题 <span className="text-primary-500">*</span>
              </label>
              <input
                id="contact-subject"
                name="subject"
                type="text"
                required
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入主题"
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                留言 <span className="text-primary-500">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                placeholder="请输入您的留言（最多500字）"
              ></textarea>
              <p className="text-xs text-foreground-400 mt-1">最多500字</p>
            </div>

            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full sm:w-auto bg-primary-500 text-background-50 px-8 py-3 rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 whitespace-nowrap"
            >
              {status === 'loading' ? '提交中...' : '发送留言'}
            </button>

            {status === 'success' && (
              <p className="text-sm text-green-600 bg-green-50 px-4 py-3 rounded-md">
                感谢您的留言！我们会尽快回复您。
              </p>
            )}
            {status === 'error' && (
              <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-md">
                {errorMessage}
              </p>
            )}
          </form>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-8">
          {[
            { icon: 'ri-mail-line', title: '邮箱', content: 'hello@henhenai.com' },
            { icon: 'ri-map-pin-line', title: '地址', content: '中国 · 北京' },
            { icon: 'ri-time-line', title: '工作时间', content: '周一至周五 9:00-18:00' },
          ].map((item) => (
            <div key={item.title} className="text-center p-5 bg-background-100 rounded-lg border border-background-200/70">
              <div className="w-10 h-10 flex items-center justify-center mx-auto rounded-full bg-primary-50 text-primary-500 mb-3">
                <i className={item.icon}></i>
              </div>
              <p className="text-sm font-medium text-foreground-900 mb-1">{item.title}</p>
              <p className="text-xs text-foreground-600">{item.content}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}